import { ApiCategory, ApiLeague } from "griptonite-api-client";
import { createContext, useContext, useEffect, useState } from "react";
import { createStore, useStore } from "zustand";
import { combine } from "zustand/middleware";

const createEntriesTableContext = () =>
  createStore(
    combine(
      {
        categoriesByUuid: {} as Record<string, ApiCategory>,
        league: undefined as ApiLeague | undefined,
        selectedCategoryUuid: null as string | null,
        competitorSearchQuery: "",
        mergeSourceUserUuid: undefined as string | undefined,
      },
      (set) => ({
        setSelectedCategoryUuid: (selectedCategoryUuid: string | null) =>
          set({ selectedCategoryUuid }),
        setCompetitorSearchQuery: (query: string) => set({ competitorSearchQuery: query }),
        clearCompetitorSearchQuery: () => set({ competitorSearchQuery: "" }),
        setMergeSourceUserUuid: (mergeSourceUserUuid: string | undefined) =>
          set({ mergeSourceUserUuid }),
      }),
    ),
  );

type State = ReturnType<ReturnType<typeof createEntriesTableContext>["getState"]>;

const EntriesTableContext = createContext<ReturnType<typeof createEntriesTableContext> | undefined>(
  undefined,
);

export const LeagueEntriesTableContextProvider = ({
  children,
  league,
}: {
  children: React.ReactNode;
  league?: ApiLeague;
}) => {
  const [store, _] = useState(createEntriesTableContext());

  useEffect(() => {
    if (league) {
      const categoriesByUuid = Object.fromEntries(
        league.categories.map((category) => [category.uuid, category]),
      );
      store.setState({
        ...store.getState(),
        categoriesByUuid,
        league,
      });
    } else {
      store.setState({
        ...store.getState(),
        categoriesByUuid: {},
        league: undefined,
      });
    }
  }, [league, store]);

  return <EntriesTableContext.Provider value={store}>{children}</EntriesTableContext.Provider>;
};

export function useLeagueEntriesTableContext(): State;
export function useLeagueEntriesTableContext<T>(selector: (state: State) => T): T;
export function useLeagueEntriesTableContext<T>(selector?: (state: State) => T) {
  const store = useContext(EntriesTableContext);

  if (!store) {
    throw new Error("useEntriesTableContext must be used within a EntriesTableContextProvider");
  }

  return useStore(store, selector!);
}
